import { useState, useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { alertActions } from '../store/alert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import HomeIcon from '@mui/icons-material/Home';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ACCAPI from '../services/api/integrations/acc-api';
import { AuthContext } from './context/auth-context';
import AlertDialog from './utilities/AlertDialog';
import Logger from '../utils/logger';
import SideNav from './SideNav';
import { useProjectRehydration } from '../Hooks/useProjectRehydration';
import { useBidRehydration } from '../Hooks/useBidRehydration';
import { useWorkspace } from '../Hooks/useWorkspace';
import { getNavGroups, navItemPath } from './navigation/navConfig';
import tokens from '../theme/tokens';

// temporarily hiding timecards and user until completed

/**
 * The Project Documents Assistant sizes its own panes to the viewport, so on
 * its route the shell gets a fixed height and the routed area scrolls. Every
 * other page keeps the shell it has on staging: a minimum height, with the
 * window scrolling.
 */
export const shellLayout = (pathname) =>
  /^\/project-documents-assistant\/?$/.test(pathname)
    ? { shell: { height: '100vh' }, routedOverflow: 'auto' }
    : { shell: { minHeight: '100vh' }, routedOverflow: 'hidden' };

export const Nav = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const user =
    Array.from(auth.userFirstName)[0] + Array.from(auth.userLastName)[0];

  // Rehydrate the globally selected project (and bid, for bids users) once
  // after authentication resolves.
  useProjectRehydration();
  useBidRehydration();

  // Responsive breakpoint detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Below 600px - show sidebar on tablets (iPad Air, iPad Pro)

  const isAdmin = auth.isAdmin;
  const isTimecard = auth.isTimecard;
  const isWorkflow = auth.isWorkflow;
  const isAnalytics = auth.isAnalytics;

  Logger.debug('Nav bar rendered', {
    isAdmin,
    isTimecard,
    isWorkflow,
    isAnalytics,
    isMobile,
  });

  // Mobile menu pages: the same nav config as the sidebar, flattened.
  // Includes the Project Assistant (#250) for super-admins once the server has
  // confirmed it is on — the condition lives in navConfig.
  const workspace = useWorkspace();
  const pages = getNavGroups(workspace.type, auth).flatMap((group) =>
    group.items.map((item) => ({
      id: item.id,
      label: item.text,
      path: navItemPath(item, workspace.type),
    }))
  );
  // Check if user is Autodesk Admin (Jeff Peterson)
  const fullName = `${auth.userFirstName} ${auth.userLastName}`;
  const isAutodeskAdmin = fullName === 'Subat Qutlan';

  // Debug logging
  Logger.debug('User info', { fullName, isAutodeskAdmin });

  const settings = isAutodeskAdmin
    ? [
        'Autodesk Sign In',
        'Autodesk Sync',
        'Autodesk Status',
        'Toggle Schedule Sync',
        'Logout',
        'Change Password',
      ]
    : ['Logout', 'Change Password'];

  const navigate = useNavigate();
  const layout = shellLayout(useLocation().pathname);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = (e) => {
    const page = e.target.id;
    Logger.debug('Closing nav menu', { page });

    const target = pages.find((p) => p.id === page);
    if (target) {
      navigate(target.path);
    }
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = (e) => {
    const userFunction = e.target.id;
    Logger.userAction('User menu action', { action: userFunction });
    if (userFunction === 'Logout') {
      auth.logout();
    } else if (userFunction === 'Change Password') {
      navigate('changepassword');
    } else if (userFunction === 'Autodesk Sign In') {
      handleAutodeskSignIn();
    } else if (userFunction === 'Autodesk Sync') {
      handleAutodeskSync();
    } else if (userFunction === 'Autodesk Status') {
      handleAutodeskStatus();
    } else if (userFunction === 'Toggle Schedule Sync') {
      handleToggleScheduleSync();
    }
    setAnchorElUser(null);
  };

  const handleAutodeskSignIn = async () => {
    try {
      // First, get the authorization URL
      const authUrl = await ACCAPI.getAuthUrl(auth.token);

      if (authUrl) {
        // Open the authorization URL in a new window
        const authWindow = window.open(
          authUrl,
          'autodesk-auth',
          'width=600,height=600,scrollbars=yes,resizable=yes'
        );

        // Listen for messages from the popup window
        const messageHandler = (event) => {
          if (event.data.type === 'AUTODESK_AUTH_SUCCESS') {
            window.removeEventListener('message', messageHandler);
            dispatch(
              alertActions.showAlert({
                title: '✅ Authentication Successful',
                message:
                  'Autodesk authentication successful! You can now sync data.',
              })
            );
          } else if (event.data.type === 'AUTODESK_AUTH_ERROR') {
            window.removeEventListener('message', messageHandler);
            dispatch(
              alertActions.showAlert({
                title: '❌ Authentication Failed',
                message: 'Authentication failed: ' + event.data.error,
              })
            );
          }
        };

        window.addEventListener('message', messageHandler);

        // Fallback: Listen for the window to close (user completed auth)
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            // Check if authentication was successful
            checkAuthStatus();
          }
        }, 1000);
      } else {
        dispatch(
          alertActions.showAlert({
            title: '❌ Authorization Error',
            message: 'Failed to get authorization URL',
          })
        );
      }
    } catch (error) {
      console.error('Error starting Autodesk sign in:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Sign In Error',
          message: 'Error starting Autodesk sign in: ' + error.message,
        })
      );
    }
  };

  const handleAutodeskSync = async () => {
    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        'This will sync all Autodesk data for all active projects. This may take several minutes. Continue?'
      );

      if (!confirmed) {
        return;
      }

      dispatch(
        alertActions.showAlert({
          title: '🔄 Autodesk Sync in Progress',
          message: 'Starting Autodesk sync... Please wait.',
          isLoading: true,
        })
      );

      // Call the sync endpoint
      const syncResult = await ACCAPI.sync(auth.token);

      // Show success message with details
      const message =
        `✅ Autodesk Sync In Progress!\n\n` +
        (syncResult.message || 'Sync started successfully');

      Logger.info('ACC sync completed', { syncResult });

      dispatch(
        alertActions.showAlert({
          title: '✅ Sync In Progress',
          message: message,
          isLoading: false,
        })
      );
    } catch (error) {
      console.error('Error during Autodesk sync:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Sync Error',
          message: 'Error during sync: ' + error.message,
          isLoading: false,
        })
      );
    } finally {
    }
  };

  const handleAutodeskStatus = async () => {
    try {
      // Show loading message
      dispatch(
        alertActions.showAlert({
          title: '🔄 Checking Status',
          message: 'Checking Autodesk status...',
        })
      );

      // Call both status endpoints
      const [syncStatus, scheduleStatus] = await Promise.all([
        ACCAPI.getSyncStatus(auth.token),
        ACCAPI.getSyncScheduleStatus(auth.token),
      ]);

      // Format the status message
      const message =
        `📊 Autodesk Sync Status\n\n` +
        `Available Services: ${syncStatus.availableServices?.join(', ') || 'N/A'}\n` +
        `Currently Running: ${syncStatus.isRunning ? 'Yes' : 'No'}\n` +
        `Last Sync: ${syncStatus.lastSync || 'Never'}\n\n` +
        `📅 Scheduled Sync:\n` +
        `Scheduled: ${scheduleStatus?.isScheduled ? 'Yes' : 'No'}\n` +
        `Next Run: ${
          scheduleStatus?.nextRun
            ? new Date(scheduleStatus.nextRun).toLocaleString()
            : 'Not scheduled'
        }\n\n` +
        `System is ready for sync operations.`;

      dispatch(
        alertActions.showAlert({
          title: '📊 Autodesk Status',
          message: message,
        })
      );
    } catch (error) {
      console.error('Error checking Autodesk status:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Status Check Error',
          message: 'Error checking status: ' + error.message,
        })
      );
    }
  };

  const handleToggleScheduleSync = async () => {
    try {
      // First, get the current schedule status
      const scheduleStatus = await ACCAPI.getSyncScheduleStatus(auth.token);

      const isCurrentlyScheduled = scheduleStatus?.isScheduled || false;
      const actionText = isCurrentlyScheduled ? 'stop' : 'start';

      // Show confirmation dialog
      const confirmed = window.confirm(
        `This will ${actionText} scheduled sync (daily at 2 AM). Continue?`
      );

      if (!confirmed) {
        return;
      }

      // Show loading message
      dispatch(
        alertActions.showAlert({
          title: `🔄 ${
            actionText.charAt(0).toUpperCase() + actionText.slice(1)
          }ing Schedule`,
          message: `${
            actionText.charAt(0).toUpperCase() + actionText.slice(1)
          }ing scheduled sync...`,
        })
      );

      // Call the appropriate endpoint
      const result = isCurrentlyScheduled
        ? await ACCAPI.stopSyncSchedule(auth.token)
        : await ACCAPI.startSyncSchedule(auth.token);

      const message =
        `✅ Scheduled sync ${actionText}ed successfully!\n\n` +
        `Schedule: Daily at 2:00 AM\n` +
        `Status: ${result.isScheduled ? 'Active' : 'Inactive'}\n` +
        `Next Run: ${
          result.nextRun
            ? new Date(result.nextRun).toLocaleString()
            : 'Not scheduled'
        }`;

      dispatch(
        alertActions.showAlert({
          title: '✅ Schedule Updated',
          message: message,
        })
      );
    } catch (error) {
      console.error(`Error toggling scheduled sync:`, error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Schedule Toggle Error',
          message: 'Error toggling scheduled sync: ' + error.message,
        })
      );
    }
  };

  const checkAuthStatus = async () => {
    try {
      const authStatus = await ACCAPI.getAuthStatus(auth.token);

      if (authStatus.authenticated) {
        // Authentication successful
        dispatch(
          alertActions.showAlert({
            title: '✅ Authentication Successful',
            message:
              'Autodesk authentication successful! You can now sync data.',
          })
        );
      } else {
        dispatch(
          alertActions.showAlert({
            title: '❌ Authentication Failed',
            message: 'Authentication failed or incomplete. Please try again.',
          })
        );
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Auth Check Error',
          message: 'Error checking authentication status: ' + error.message,
        })
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', ...layout.shell }}>
      {/* SideNav - only shown on desktop/tablet (above 960px) */}
      {!isMobile && <SideNav />}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mobile Navigation - AppBar with hamburger menu (only shown on mobile < 960px) */}
        {isMobile && (
          <AppBar position="sticky">
            <Container maxWidth="xl">
              <Toolbar disableGutters>
                <Box sx={{ flexGrow: 1, display: 'flex' }}>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                  >
                    {pages.map((page) => (
                      <MenuItem
                        key={page.id}
                        id={page.id}
                        onClick={handleCloseNavMenu}
                      >
                        <Typography
                          key={page.id}
                          id={page.id}
                          textAlign="center"
                        >
                          {page.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
                <Typography
                  variant="h5"
                  noWrap
                  component="a"
                  href=""
                  sx={{
                    mr: 2,
                    flexGrow: 1,
                    fontFamily: tokens.fontFamily,
                    fontWeight: tokens.fontWeights.semibold,
                    letterSpacing: '.3rem',
                    color: 'inherit',
                    textDecoration: 'none',
                  }}
                >
                  P1
                </Typography>

                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar>{user}</Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    {settings.map((setting) => (
                      <MenuItem
                        key={setting}
                        id={setting}
                        onClick={handleCloseUserMenu}
                      >
                        <Typography id={setting} textAlign="center">
                          {setting}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Toolbar>
            </Container>
          </AppBar>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: layout.routedOverflow,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </Box>
        <AlertDialog />
      </Box>
    </Box>
  );
};
